import React from 'react';
import moment from 'moment-timezone';
import {
  Modal,
  ModalSection,
  Gutter,
  CalendarTimeZoneUtil,
  TimezoneDropdown,
  Form,
  Textarea,
  ParagraphHeader,
} from '@axiom/ui';
import type { CalendarManualInterview, TimeZone } from '@axiom/validation';
import { NO_TIMEZONE } from '@axiom/const';

import type { InterviewFlowCandidate } from '../../types/interview-candidate.types';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';

import { TalentInterviewPromptModalSchema } from './talent-interview-prompt-modal-schema';

export interface PromptModalProps {
  candidate: InterviewFlowCandidate;
  onClose: () => void;
  onContinue: (
    id: TimeZone['id'],
    notes: CalendarManualInterview['availabilityNote']
  ) => void;
}

export const TalentInterviewPromptModal = ({
  candidate,
  onClose,
  onContinue,
}: PromptModalProps) => {
  const talentIana =
    'calendar' in candidate ? candidate.calendar?.timezone : undefined;
  const guessIana = moment.tz.guess();
  const timezone = CalendarTimeZoneUtil.getZoneFromMomentZone(
    moment.tz(new Date(), talentIana || guessIana)
  );
  return (
    <Form
      name="INTERVIEW_PROMPT_FORM"
      initialValues={{
        TIMEZONE_DROPDOWN: timezone === NO_TIMEZONE ? undefined : timezone?.id,
      }}
      schema={TalentInterviewPromptModalSchema}
      onSubmit={formData => {
        const { TIMEZONE_DROPDOWN, AVAILABILITY_NOTES } = formData;
        onContinue(
          TIMEZONE_DROPDOWN ?? timezone?.id ?? NO_TIMEZONE.id,
          AVAILABILITY_NOTES
        );
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Modal name="INTERVIEW_PROMPT_MODAL">
            <TalentInterviewModalHeader
              candidate={candidate}
              name="INTERVIEW_PROMPT_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            />
            <ModalSection>
              <ParagraphHeader name="SECTION_DESCRIPTION">
                Select your time zone and suggest times to chat with{' '}
                {candidate.calculatedFirstName ||
                  candidate.calculatedDisplayName}
                .
              </ParagraphHeader>
              <Gutter bottom="16px" />
              <TimezoneDropdown
                label="Your time zone"
                name="TIMEZONE_DROPDOWN"
              />
              <Gutter bottom="16px" />
              <Textarea
                label="Suggest 2 or more days and times for a 30-minute interview."
                description="Include the day, date, and time. Ex: Monday 9/16/2024 at 1pm."
                name="AVAILABILITY_NOTES"
              />
            </ModalSection>
            <TalentInterviewModalFooter
              name="INTERVIEW_PROMPT_MODAL_FOOTER"
              onClose={() => {
                onClose();
              }}
              onContinue={async () => {
                await fireSubmit();
              }}
            />
          </Modal>
        );
      }}
    </Form>
  );
};
